import React, { useState, useEffect, useCallback } from "react";
import { api } from "../api/client";
import { requestAndSubscribePush } from "../hooks/usePushNotifications";
import useGoogleCalendarEvents from "../hooks/useGoogleCalendarEvents";
import useICalEvents from "../hooks/useICalEventsNew";
import { useOutlookEvents } from "../hooks/useOutlookEvents";
import { NotesTile } from "./Notes/DashboardNotes";
import { priorityRank } from "../utils/constants";
import { isGoogleCalendarTask, isICalTask, isOutlookTask } from "../utils/calendarUtils";
import { isCalendarEventTask, taskDisplayTitle } from "../utils/calendarTask";   // Fresh-eyes 19
import { useCurrentUser } from "../contexts/CurrentUserContext";
import MorningBriefing from "./Briefing/MorningBriefing";
import OnboardingChecklist from "./OnboardingChecklist";
import NotificationFeed from "./FollowUp/NotificationFeed";
import { personalFlag } from "../nav/views";
import FollowUpPanel from "./FollowUp/FollowUpPanel";
import PulseWidget from "./Pulse/PulseWidget";
import LeaveRequests from "./LeaveRequests";
import TeamOverviewTile from "./Team/TeamOverviewTile";
import useAutoTour from '../tours/useAutoTour';
import useChatIntro from '../tours/useChatIntro';
import { messageFromError } from '../utils/apiError';   // Block 233
import { showErrorToast } from '../utils/toast';        // Block 233
import { formatTaskTimeRange, formatTaskClock } from '../utils/taskTime';
import { filterMyTasks } from '../utils/myTasks';   // fix(task-time), fix(task-dates)
import { Button, DashboardCard, StatusPill, EmptyState, Banner, Avatar, Input, Select, TabGroup, PageHeader, PRIORITY_OPTIONS, useBreakpoint, HeaderButton, HeaderAction, AddTaskButton } from '../ui';
import './dashboard.css';



// ── Family Chat dashboard widget ──────────────────────────────────────────────
function FamilyChatWidget({ onOpen }) {
  const { currentUser } = useCurrentUser();
  const [messages,       setMessages]       = useState([]);
  const [participantCount, setParticipantCount] = useState(0);
  const [reply,          setReply]          = useState('');
  const [sending,        setSending]        = useState(false);
  const [ready,          setReady]          = useState(false);

  const loadMessages = useCallback(() => {
    api('/api/family-chat')
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setMessages(d.messages || []);
        setParticipantCount(d.participantCount ?? 0);
        setReady(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadMessages();
    const t = setInterval(loadMessages, 20000);
    return () => clearInterval(t);
  }, [loadMessages]);

  if (!ready || participantCount < 2) return null;

  const preview = messages.slice(-5);

  async function handleSend(e) {
    e.preventDefault();
    const body = reply.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await api('/api/family-chat', { method: 'POST', body: JSON.stringify({ body }) });
      setReply('');
      loadMessages();
    } catch (err) { showErrorToast(messageFromError(err, "Could not send that message.")); }
    finally  { setSending(false); }
  }

  function formatTs(iso) {
    const d = new Date(iso);
    const now = new Date();
    const diffMins = Math.floor((now - d) / 60000);
    if (diffMins < 1)  return 'just now';
    if (diffMins < 60) return `${diffMins}m`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }

  return (
    <DashboardCard tint="pastel-lilac" title="Family chat"
      meta={<HeaderAction onClick={onOpen}>Open →</HeaderAction>}>

      {preview.length === 0 ? (
        <p className="dash-text">No messages yet. Say hello!</p>
      ) : (
        <ul className="dash-notifs">
          {preview.map(msg => {
            const isMe = msg.senderUserId === currentUser?.id;
            return (
              <li key={msg.id} className="dash-msg">
                <Avatar name={isMe ? (currentUser?.name || 'You') : msg.senderName} size={24} color={isMe ? 'pastel-blue' : 'pastel-pink'} />
                <span className="dash-msg__main">
                  <span className="dash-msg__who">
                    <span className="dash-msg__name">{isMe ? 'You' : msg.senderName}</span>
                    <span className="dash-meta">{formatTs(msg.createdAt)}</span>
                  </span>
                  <span className="dash-msg__body">{msg.body}</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <form onSubmit={handleSend} className="dash-composer">
        <Input
          value={reply}
          onChange={e => setReply(e.target.value)}
          placeholder="Quick reply…"
          aria-label="Quick reply to family chat"
          disabled={sending}
        />
        <Button type="submit" variant="confirm" disabled={!reply.trim() || sending}>Send</Button>
      </form>
    </DashboardCard>
  );
}

function Dashboard({
  tasksLoadFailed = false,
  today,
  dashboardSortBy,
  setDashboardSortBy,
  getTodayTasks,
  setViewTask,
  taskTypes,
  quickNotes,
  deleteNote,
  renderProductivityView,
  setFilter,
  tasks,
  handleAddTaskFromChat,
  handleAideyEditTask,
  handleAideyDeleteTask,
  clashInfo,
  noteCategories = [],
  setNoteCategories,
  markComplete,
  onAddTask,       // Block B: the header's "+ Add task" (App.js openAddTaskModal)
}) {
  const breakpoint = useBreakpoint();

  // Get current user context
  const { currentUser, notifications, dismissNotification, markNotificationAsRead, activeWorkspaceId, workspaces, isTeamAdmin } = useCurrentUser();
  const activeWorkspace = (workspaces || []).find(w => w.id === activeWorkspaceId) || null;
  const isPersonalWorkspace = activeWorkspace?.type === 'personal';

  // Filter notifications to the active workspace so business-workspace notifications
  // (leave requests, invoice alerts) do not bleed into the personal dashboard.
  // Workspace scoping is now enforced server-side (GET /api/notifications filters
  // by the active workspace + globals), so only the priority/state filter remains.
  const normalNotifications = (notifications || []).filter(n =>
    n.priority === 'normal' && !n.dismissed && !n.read
  );
  useAutoTour('dashboard');
  useChatIntro('dashboard');

  // PWA push prompt — check in useEffect only, never in useState initialiser
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'default') return;
    if (!currentUser) return;
    if (currentUser?.preferences?.notifications?.promptDismissed) return;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) setShowPushPrompt(true);
  }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handlePushEnable() {
    setShowPushPrompt(false);
    await requestAndSubscribePush();
  }

  async function handlePushDismiss() {
    setShowPushPrompt(false);
    try {
      // This writes a key INSIDE the `notifications` sub-object, which the server
      // REPLACES rather than merges — so it has to know the current value, and
      // currentUser.preferences is a login-time snapshot. It used to spread that
      // snapshot at the TOP level too, reverting every other screen's save.
      //
      // One extra read on a rare action (dismissing a prompt) is the honest price of
      // not guessing. See Settings/WritingStyle.jsx for the top-level/sub-object rule.
      const fresh = await api('/api/users/me').then(r => r.json()).catch(() => null);
      const current = fresh?.user?.preferences?.notifications;
      await api('/api/users/me', {
        method: 'PUT',
        body: JSON.stringify({
          preferences: {
            notifications: {
              ...(current && typeof current === 'object' ? current : (currentUser?.preferences?.notifications || {})),
              promptDismissed: true,
            },
          },
        }),
      });
    } catch (err) { showErrorToast(messageFromError(err, "Could not dismiss that.")); }
  }

  const [users, setUsers] = useState([]);

  // Team overview, Leave requests and Team chat: only when the business has the Team Pro package, and
  // the SERVER says so (GET /api/team/dashboard-sections; Carla's item 7, 2026-10-12). This read the
  // plan string the page held, and let any trial in. Hidden until the answer comes; a failed check is
  // said (teamSectionsError), never shown as "no team".
  const [teamSections, setTeamSections] = useState(false);
  const [teamSectionsError, setTeamSectionsError] = useState('');
  const [teamSectionsTry, setTeamSectionsTry] = useState(0);
  useEffect(() => {
    if (!currentUser?.id || isPersonalWorkspace) { setTeamSections(false); setTeamSectionsError(''); return undefined; }
    let live = true;
    (async () => {
      try {
        const res = await api('/api/team/dashboard-sections');
        const data = await res.json();
        if (!live) return;
        setTeamSections(data?.teamSections === true);
        setTeamSectionsError('');
      } catch (err) {
        if (!live) return;
        setTeamSections(false);
        setTeamSectionsError(messageFromError(err, 'Please try again.'));
      }
    })();
    return () => { live = false; };
  }, [currentUser?.id, activeWorkspaceId, isPersonalWorkspace, teamSectionsTry]);
  const hasTeamAccess = teamSections;

  // Team Chat — persisted to backend API
  const [activeTab, setActiveTab] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  // ── THE SECOND SURFACE (Carla, re-test 3.3, 2026-10-04) ──────────────────
  // "You fixed /team. The Dashboard has its own chat UI and it is still the old one.
  // Second-path rule, on the very fix written for a second-path defect."
  //
  // Correct. /api/team-chat now answers {noTeam, reason} when the user's business cannot be
  // resolved, and refuses the POST with a 409 rather than storing a message nobody can read.
  // TeamDashboard was taught to read that; THIS component was not, so it kept showing
  // "No messages yet. Say something to the whole team!" — a cheerful invitation to use a
  // feature that cannot work — and then silently DELETED each message it failed to send.
  const [noTeam, setNoTeam] = useState(false);
  const [noTeamReason, setNoTeamReason] = useState('');
  const [chatError, setChatError] = useState('');

  const fetchMessages = useCallback(() => {
    if (!hasTeamAccess || !currentUser) return;
    api('/api/team-chat')
      .then(r => r.json())
      .then(d => {
        if (Array.isArray(d.messages)) setMessages(d.messages);
        // noTeam is the SERVER'S answer, not an inference from an empty list. An empty list
        // and "you have no team" look identical from here, and telling someone with no team
        // to "say something to the whole team" is the lie she reported.
        setNoTeam(!!d.noTeam);
        setNoTeamReason(typeof d.reason === 'string' ? d.reason : '');
      })
      .catch(() => {});
  }, [hasTeamAccess, currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load real team members from the backend API (not the demo localStorage users)
  useEffect(() => {
    if (!hasTeamAccess || !currentUser) return;

    api('/api/team/members')
      .then(res => res.json())
      .then(data => {
        const members = (data.members || []).filter(m => m.id !== currentUser.id);
        setUsers(members);
        if (members.length > 0 && !activeTab) setActiveTab("all");
      })
      .catch(() => setUsers([]));

  }, [currentUser?.id, hasTeamAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch messages and poll every 30 s
  useEffect(() => {
    if (!hasTeamAccess || !currentUser) return;
    fetchMessages();
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, [fetchMessages]);


  // Scroll to top state and functionality
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Get Google Calendar events
  const { events: googleEvents } = useGoogleCalendarEvents();

  // Get iCal events from user profile
  let userProfile = {};
  try { userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}"); } catch { /* use empty default */ }
  // The ACCOUNT first — see App.js. A feed subscribed on another device used to be
  // invisible here, with nothing to say so.
  const icalUrl = currentUser?.preferences?.icalUrl
    || (userProfile.integrations?.ical?.connected ? userProfile.integrations.ical.url : null);
  const { events: icalEvents } = useICalEvents(icalUrl);

  // Get Outlook events
  const { events: outlookEvents } = useOutlookEvents();

  // Your tasks by the ownership rule (utils/myTasks; Carla, 2026-10-11).
  const filterUserSpecificTasks = (tasks) => filterMyTasks(tasks, currentUser, (task) => isGoogleCalendarTask(task) || isICalTask(task) || isOutlookTask(task));

  // Fresh-eyes 19: a meeting has no priority, so it sorts with the middle of the list rather
  // than by the "High" App.js's due-soon escalation gives everything due today.
  const rankOf = (t) => (isCalendarEventTask(t) ? priorityRank.Medium : (priorityRank[t.priority] ?? priorityRank.Medium));

  // ✅ ORIGINAL TOP TASKS LOGIC - More efficient and focused
  const getTopFiveTasks = () => {
    const now = new Date();
    // Get only Aidey tasks (not external calendar events for top tasks)
    const aideyTasks = getTodayTasks();

    // Step 1: Filter for tasks due today (and not completed)
    const relevantTasks = aideyTasks.filter((task) => {
      if (task.completed) return false;
      const taskDate = new Date(task.datetime);
      return taskDate.toDateString() === now.toDateString();
    });

    // Apply user filtering
    const userFilteredTasks = filterUserSpecificTasks(relevantTasks);

    // Step 2: Sort by priority first, then by start time
    const sorted = [...userFilteredTasks].sort((a, b) => {
      const aTime = new Date(a.datetime);
      const bTime = new Date(b.datetime);

      const isAOverdue = aTime < now;
      const isBOverdue = bTime < now;

      // Overdue tasks come first
      if (isAOverdue !== isBOverdue) {
        return isAOverdue ? -1 : 1;
      }

      // Then sort by priority
      const priorityOrder = rankOf(a) - rankOf(b);
      if (priorityOrder !== 0) return priorityOrder;

      // Then by start time
      return aTime - bTime;
    });

    return sorted.slice(0, 5); // Limit to top 5
  };

  // ✅ DASHBOARD SORT FUNCTION
  const sortDashboardTasks = (tasks) => {
    return dashboardSortBy === "time"
      ? [...tasks].sort((a, b) => {
        // Handle tasks without time
        if (!a.time && !b.time) return 0;
        if (!a.time) return 1;
        if (!b.time) return -1;
        return a.time.localeCompare(b.time);
      })
      : [...tasks].sort((a, b) => {
        const priorityOrder = rankOf(a) - rankOf(b);
        if (priorityOrder !== 0) return priorityOrder;
        // Sort by time as secondary
        if (!a.time && !b.time) return 0;
        if (!a.time) return 1;
        if (!b.time) return -1;
        return a.time.localeCompare(b.time);
      });
  };

  // ✅ USE ORIGINAL TOP TASKS APPROACH
  const allTopTasks = getTopFiveTasks(); // up to 5 tasks
  const sortedTopTasks = sortDashboardTasks(allTopTasks);
  const topTasks = sortedTopTasks.slice(0, 5); // show top 5


  // Handle sending chat messages
  const handleSend = () => {
    if (!newMessage.trim()) return;
    // A DISABLED BUTTON IS NOT A GUARD. The Enter key calls this directly, so the refusal
    // lives here as well as on the control — otherwise the one path that is quickest to use
    // is the one that is unprotected.
    if (noTeam) {
      setChatError(noTeamReason || 'Team chat needs a team. Invite someone in Settings → Team and you can both use it.');
      return;
    }
    const body = newMessage.trim();
    setNewMessage('');
    const optimistic = { id: `opt-${Date.now()}`, user_id: currentUser.id, sender_name: currentUser.name, body, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, optimistic]);
    setChatError('');
    api('/api/team-chat', { method: 'POST', body: JSON.stringify({ body }) })
      .then(r => r.json())
      .then(d => {
        if (d.message) setMessages(prev => prev.map(m => m.id === optimistic.id ? d.message : m));
      })
      .catch((err) => {
        // IT USED TO REMOVE THE MESSAGE AND SAY NOTHING. Her words from the first sitting
        // were "the message did not stay on screen" — this is that code. A row appearing and
        // then vanishing with no explanation is worse than a refusal, because the only
        // reading available is that Aidey lost it.
        //
        // The server's own sentence is used when there is one: the 409 for a user with no
        // team already explains what to do (invite someone in Settings → Team).
        setMessages(prev => prev.filter(m => m.id !== optimistic.id));
        const own = err && err.body && typeof err.body.error === 'string' ? err.body.error : '';
        setChatError(own || 'That message was not sent. Nothing was saved, please try again.');
        // Put the text back in the box, so she is not retyping what Aidey failed to send.
        setNewMessage(body);
        if (err && err.status === 409) { setNoTeam(true); if (own) setNoTeamReason(own); }
      });
  };

  // Format message timestamp
  const formatMessageTime = (timestamp) => formatTaskClock(timestamp);   // fix(task-dates): the shared formatter

  // ── Block B: the page, on the v2 kit (mockups/business-dashboard.html) ──────────────
  const phone = breakpoint === 'phone';
  const isExternal = (task) => isGoogleCalendarTask(task) || isICalTask(task) || isOutlookTask(task);
  const priorityKey = (p) => {
    const k = String(p || '').toLowerCase();
    return PRIORITY_OPTIONS.some(o => o.value === k) ? k : null;
  };
  const todayLabel = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  // Insights is a Business page, offered only there: from the header on a tablet or desktop, and
  // on a phone from a "View insights →" link in the Workload card (decision 86), never both.
  const showInsights = !isPersonalWorkspace;
  // Through the app's navigation (view AND address), so a reload stays on Insights.
  const openInsights = () => window.dispatchEvent(new CustomEvent('aidey:navigate', { detail: '/insights' }));
  const addTaskButton = onAddTask ? <AddTaskButton onClick={onAddTask} /> : null;
  const showTeam = hasTeamAccess && !isPersonalWorkspace;

  const chatPeople = users.filter(u => u.id !== currentUser?.id);
  const chatTabs = [
    { id: 'all', label: '👥 All team', color: 'highlight' },
    ...chatPeople.map(u => ({ id: u.id, label: u.name, color: 'pastel-lilac' })),
  ];

  return (
    <div className="dash">
      {showPushPrompt && (
        <Banner
          tone="info"
          onDismiss={handlePushDismiss}
          action={<Button variant="confirm" size="sm" onClick={handlePushEnable}>Enable</Button>}
        >
          <span aria-hidden="true">🔔 </span>Get notified when someone books with you
        </Banner>
      )}

      <PageHeader
        eyebrow={{ label: isPersonalWorkspace ? 'Personal workspace' : 'Business workspace', color: 'group-overview' }}
        title="Dashboard"
        subline={todayLabel}
        help="dashboard"
        // Decision 86: on a phone + Add task is the whole row; "View insights →" is in the Workload card.
        primary={phone ? addTaskButton : (
          <>
            {showInsights && <HeaderButton onClick={openInsights}>View insights</HeaderButton>}
            {addTaskButton}
          </>
        )}
      />

      <OnboardingChecklist onNavigate={setFilter} tasks={tasks} />

      {/* Row 1: the briefing and the workload, side by side on desktop. */}
      {/* Decision 87: one grid (auto-fit, minmax(min(100%, 420px), 1fr), 24px gap); each tile fills its cell. */}
      <section className="dash-grid" data-tour-id="dashboard-stats" aria-label="Today">
        <div className="dash-col" data-tour-id="dashboard-briefing">
          <MorningBriefing
            tasks={tasks}
            googleEvents={googleEvents}
            icalEvents={icalEvents}
            outlookEvents={outlookEvents}
          />
        </div>
        <div className="dash-col">
          <PulseWidget insightsLink={phone && showInsights ? openInsights : null} />
        </div>
      </section>

      {/* Row 2: follow-ups on the left; tasks, notifications and notes on the right. */}
      {/* Decision 87 (mockups/business-dashboard.html): Follow-up needed and Top tasks on the left,
          Notifications and Notes on the right, as two rows so each pair lines up top and bottom.
          One column: Follow-up, Top tasks, Notifications, Notes. Personal: family chat in Follow-up's place. */}
      <section className={`dash-work${isPersonalWorkspace ? ' dash-work--personal' : ''}`} aria-label="Your work">
        {!isPersonalWorkspace && (
          <div className="dash-area dash-area--follow" data-tour-id="dashboard-followups">
            {/* FU-1: always shown (empty state, load failure, top 3 and "See all"), never hidden. */}
            <FollowUpPanel onNavigate={setFilter} />
          </div>
        )}

        <div className="dash-area dash-area--tasks" data-tour-id="dashboard-week">
          {/* --- Top tasks, with the sort --- */}
          <DashboardCard tint="pastel-blue" title="Top tasks" meta={(
            <label className="dash-sort" htmlFor="dashboardSort">
              Sort by
              <Select
                id="dashboardSort"
                value={dashboardSortBy}
                onChange={(e) => setDashboardSortBy(e.target.value)}
                options={[{ value: 'priority', label: 'Priority' }, { value: 'time', label: 'Time' }]}
              />
            </label>
          )}>
            {tasksLoadFailed ? (
              <p data-testid="dashboard-tasks-failed" className="dash-failed" role="alert">
                Your tasks could not be loaded. This does not mean you have none.
                Refresh to try again.
              </p>
            ) : topTasks.length === 0 ? (
              <EmptyState
                icon={<span className="dash-check">✓</span>}
                action={<Button variant="confirm" size="sm" onClick={() => setFilter("Tasks")}>View all tasks</Button>}
              >
                No tasks due today, you're all clear.
              </EmptyState>
            ) : (
              <>
                <ul className="dash-tasks">
                  {topTasks.map((task) => {
                    const external = isExternal(task);
                    // Fresh-eyes 19: a booking or synced event is a meeting: no priority pill.
                    const meeting = !external && isCalendarEventTask(task);
                    const pk = meeting ? null : priorityKey(task.priority);
                    const typeLabel = !external && !meeting && task.taskType
                      ? String(taskTypes.find((t) => t.value === task.taskType)?.label || '')
                      : '';
                    const when = (isGoogleCalendarTask(task) || isICalTask(task))
                      ? (task.time || "All day")
                      : formatTaskTimeRange(task);   // fix(task-time): no end time from an empty deadline
                    return (
                      <li
                        key={task.id}
                        className={`dash-task${external ? '' : ' dash-task--open'}`}
                        onClick={() => !external && setViewTask(task)}
                      >
                        {/* Inline complete button: only for Aidey tasks */}
                        {!external && markComplete && (
                          <button
                            type="button"
                            className="dash-task__check ui-hit"
                            onClick={e => { e.stopPropagation(); markComplete(task.id); }}
                            title="Mark complete"
                            aria-label={`Mark ${String(task.title)} complete`}
                          >
                            ✓
                          </button>
                        )}
                        <span className="dash-task__main">
                          <span className="dash-task__title">{taskDisplayTitle(task)}</span>
                          <span className="dash-task__line">
                            {meeting && <StatusPill tone="neutral">Meeting</StatusPill>}
                            {when && <span>{when}</span>}
                            {typeLabel && <span>{typeLabel}</span>}
                            {isGoogleCalendarTask(task) && <StatusPill tone="neutral">📅 Google</StatusPill>}
                            {isICalTask(task) && <StatusPill tone="neutral">📅 iCal</StatusPill>}
                            {isOutlookTask(task) && <StatusPill tone="neutral">🔷 Outlook</StatusPill>}
                            {!external && task.assignedTeam && task.assignedTeam.length > 1 && (
                              <span title={`Collaborative task with ${task.assignedTeam.length} team members`}>
                                <StatusPill tone="neutral">👥 Team</StatusPill>
                              </span>
                            )}
                            {!external && pk && <StatusPill priority={pk} />}
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {allTopTasks.length > 5 && (
                  <div className="dash-actions">
                    <Button variant="confirm" size="sm" onClick={() => setFilter("Tasks")}>View all tasks</Button>
                  </div>
                )}
              </>
            )}
          </DashboardCard>
        </div>

        <div className="dash-area dash-area--notifs">
          {/* Block 203b: was FollowUpEngine; the notification feed keeps its cards and behaviour. */}
          <NotificationFeed
            notifications={normalNotifications}
            onDismiss={dismissNotification}
            onNavigate={setFilter}
            onRead={markNotificationAsRead}
            isPersonal={personalFlag(workspaces, activeWorkspaceId)}
          />
        </div>

        <div className="dash-area dash-area--notes">
          {/* Item 6 redo: the 3 most recent notes; quick capture behind "+ New note" (Notes/DashboardNotes.jsx). */}
          <NotesTile notes={quickNotes} categories={noteCategories} setNoteCategories={setNoteCategories}
            onOpenNotes={() => setFilter("Notes")}
            onDeleted={() => window.dispatchEvent(new Event("aidey:notesUpdated"))}
            onCreated={() => window.dispatchEvent(new Event("aidey:notesUpdated"))} />
        </div>

        {/* Family Chat widget: personal workspace only, self-hides when < 2 participants */}
        {isPersonalWorkspace && (
          <div className="dash-area dash-area--family"><FamilyChatWidget onOpen={() => setFilter('FamilyChat')} /></div>
        )}
      </section>

      {/* Team: Team overview, Leave requests and Team chat. Only when the server says the business
          has Team Pro (Carla's item 7); hidden in the personal workspace. */}
      {!isPersonalWorkspace && teamSectionsError && (
        <Banner tone="problem" action={<Button size="sm" variant="secondary" onClick={() => setTeamSectionsTry(n => n + 1)}>Try again</Button>}>
          Your team sections could not be loaded. {teamSectionsError}
        </Banner>
      )}
      {showTeam && (
        <section className="dash-section" aria-labelledby="dash-team-title">
          <div className="dash-section__head">
            <span className="dash-section__dot" aria-hidden="true" />
            <h2 className="dash-section__title" id="dash-team-title">Team</h2>
          </div>

          {/* Team overview | Leave requests, one height; Team chat full width below. */}
          <div className="dash-grid">
            {/* Fresh-eyes 12: the team route's own numbers (GET /api/tasks?team=true, its summary), the
                same ones /team shows; it counted this page's own task list and said "No team task data yet". */}
            <TeamOverviewTile isTeamAdmin={isTeamAdmin} onOpenTeam={() => setFilter("TeamDashboard")} />

            {/* Leave Requests: component self-hides for non-managers */}
            <LeaveRequests />
          </div>

          {/* Team Chat */}
          <DashboardCard tint="pastel-orange" title="Team chat" titleAs="h3" className="dash-teamchat">

            {chatPeople.length === 0 ? (
              <EmptyState
                icon="👥"
                title="No team members yet"
                action={<Button variant="create" onClick={() => setFilter("TeamManagement")}>Invite team members</Button>}
              >
                Invite your first team member to get started.
              </EmptyState>
            ) : activeTab ? (
              <>
                <TabGroup label="Team chat" tabs={chatTabs} value={activeTab} onChange={setActiveTab} idPrefix="dash-chat" />

                <div className="dash-chat__list" role="log" aria-label="Team chat messages">
                  {(() => { const visible = activeTab === 'all' ? messages : messages.filter(m => m.user_id === activeTab); return visible.length === 0 ? (
                    <p className="dash-chat__empty">
                      {/* THE NO-TEAM BRANCH IS CHECKED FIRST. Otherwise it is unreachable: a
                          user with no team also has no messages. */}
                      {noTeam
                        ? (noTeamReason || 'Team chat needs a team. Invite someone in Settings → Team and you can both use it.')
                        : activeTab === "all"
                          ? "No messages yet. Say something to the whole team!"
                          : `No messages yet. Start the conversation with ${(users || []).find(u => u.id === activeTab)?.name || 'your teammate'}!`}
                    </p>
                  ) : (
                    visible.map((msg) => (
                      <div key={msg.id} className="dash-msg">
                        <Avatar name={msg.sender_name} size={36} color={msg.user_id === currentUser?.id ? 'pastel-blue' : 'pastel-lilac'} />
                        <div className="dash-msg__main">
                          <div className="dash-msg__who">
                            <span className="dash-msg__name">{msg.sender_name}</span>
                            {msg.created_at && <span className="dash-meta">{formatMessageTime(msg.created_at)}</span>}
                          </div>
                          <div className="dash-msg__body">{msg.body}</div>
                        </div>
                      </div>
                    ))
                  ); })()}
                </div>

                {/* THE MESSAGE BELONGS AT THE CONTROL THAT FAILED: directly above the box she typed in. */}
                {chatError && (
                  <p data-testid="dashboard-team-chat-error" className="dash-error" role="alert">{chatError}</p>
                )}

                <div className="dash-composer">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={noTeam}
                    aria-label="Team chat message"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSend();
                      }
                    }}
                    placeholder={noTeam
                      ? "You need a team before you can use team chat"
                      : activeTab === "all" ? "Message the team…" : `Message ${users.find(u => u.id === activeTab)?.name}…`}
                  />
                  <Button variant="confirm" onClick={handleSend} disabled={!newMessage.trim() || noTeam}>Send</Button>
                </div>
                {!phone && <p className="dash-hint">Press Enter to send</p>}
              </>
            ) : (
              <p className="dash-text">Select a team member to start chatting.</p>
            )}
          </DashboardCard>
        </section>
      )}

      {/* Scroll to top */}
      {showScrollTop && (
        <button type="button" onClick={scrollToTop} className="dash-scrolltop" title="Scroll to top" aria-label="Scroll to top">
          ↑
        </button>
      )}
    </div>
  );
}

export default Dashboard;
